import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostBinding,
  HostListener,
  Input,
  NgModule,
  OnInit,
  Optional,
} from '@angular/core';
import { ControlValueAccessor, FormGroupDirective, NG_VALUE_ACCESSOR, NgForm } from '@angular/forms';
import { slideDown } from '@commons/animations/slide';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'app-toggle',
  templateUrl: './toggle.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ToggleComponent),
      multi: true,
    },
  ],
  animations: [slideDown()],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToggleComponent implements ControlValueAccessor, OnInit {
  private static Counter = 0;

  set value(val: boolean) {
    if (!!val && this._value !== val) {
      this._value = val;
      this.onChange(val);
      this.onTouch();
    }
  }

  @HostBinding('attr.checked')
  @HostBinding('class.wzb-toggle--activated')
  _value!: boolean;

  @HostBinding('attr.disabled')
  isDisabled = false;

  @Input() forceError!: boolean;
  @Input() hideLabel = false;

  readonly id = `wzb-toggle-${++ToggleComponent.Counter}`;

  constructor(
    readonly element: ElementRef,
    private readonly cdr: ChangeDetectorRef,
    @Optional() private readonly form: FormGroupDirective,
    @Optional() private readonly ngForm: NgForm
  ) {}

  ngOnInit(): void {
    if (this.form) {
      this.form.ngSubmit.pipe(untilDestroyed(this)).subscribe({ next: () => this.cdr.detectChanges() });
    }

    if (this.ngForm) {
      this.ngForm.ngSubmit.pipe(untilDestroyed(this)).subscribe({ next: () => this.cdr.detectChanges() });
    }
  }

  onChange: (_: boolean) => void = () => {
    // intentional empty arrow function
  };
  onTouch: () => void = () => {
    // intentional empty arrow function
  };

  @HostListener('click', ['$event'])
  onClick(event: any): void {
    this.changeValue(event);
  }

  @HostListener('keydown.enter', ['$event'])
  onKeydownEnter(event: any): void {
    this.changeValue(event);
  }

  private changeValue(event: any) {
    event.preventDefault();

    if (this.isDisabled) {
      return;
    }

    this.writeValue(!this._value);
    this.onChange(this._value);
  }

  writeValue(value: boolean): void {
    this._value = value;
    this.cdr.detectChanges();
  }

  registerOnChange(fn: any): void {
    this.onChange = (value: boolean) => {
      fn(value);
      this.cdr.detectChanges();
    };
  }

  registerOnTouched(fn: any): void {
    this.onTouch = () => {
      fn();
      this.cdr.detectChanges();
    };
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.cdr.detectChanges();
  }
}

@NgModule({
  imports: [CommonModule],
  declarations: [ToggleComponent],
  exports: [ToggleComponent],
})
export class ToggleModule {}
